import React, { useState, useEffect } from "react";
import { Button, Tooltip, Spinner } from "@fluentui/react-components";
import { FolderArrowRightRegular, FolderOpenRegular } from "@fluentui/react-icons";
import { useMutation } from "@tanstack/react-query";

import FileBrowse from "../../router/AuthorizedRoutes/FileBrowse";
import { buttonStackClass, suggestionButtonClass } from "./styles";
import { useFilingContext } from "../../contexts/FilingContext";
import useAuthContext from "../../hooks/useAuthContext";
import { fileItems } from "../../api/file";
import { suggestedFolder } from "../../types/following";

type Props = {
  itemIds: string[];
  onSuccess?: (folder: suggestedFolder) => void;
};

const Filing: React.FC<Props> = ({ itemIds, onSuccess }) => {
  const { suggestedFolders, setLastFiledFolder } = useFilingContext();
  const visibleSuggestions = suggestedFolders.slice(0, 3);
  const [showBrowse, setShowBrowse] = useState(false);

  useEffect(() => {
    setShowBrowse(suggestedFolders.length === 0);
  }, [itemIds, suggestedFolders]);
  const { tokenData } = useAuthContext();
  const msUserId = tokenData.oid;

  const { mutate: doFile, isPending: isFiling, variables: filingFolder } = useMutation({
    mutationFn: (folder: suggestedFolder) => fileItems(msUserId, itemIds, folder.graphFolderId),
    onSuccess: (_, folder) => {
      setLastFiledFolder(folder);
      onSuccess?.(folder);
    },
  });

  return (
    <>
      <div className={buttonStackClass}>
        {visibleSuggestions.length > 0 ? (
          visibleSuggestions.map((folder) => (
            <Tooltip
              key={folder.graphFolderId}
              content={folder.graphFolderPath || folder.graphFolderName}
              relationship="label"
            >
              <Button
                icon={isFiling && filingFolder?.graphFolderId === folder.graphFolderId ? <Spinner size="tiny" /> : <FolderArrowRightRegular />}
                disabled={isFiling}
                className={suggestionButtonClass}
                onClick={() => doFile(folder)}
              >
                {folder.graphFolderName}
              </Button>
            </Tooltip>
          ))
        ) : (
          <>
            <Button disabled className={suggestionButtonClass}>No suggestions yet</Button>
          </>
        )}
        {!showBrowse && (
          <Button appearance="secondary" icon={<FolderOpenRegular />} className={suggestionButtonClass} onClick={() => setShowBrowse(true)}>
            Browse
          </Button>
        )}
      </div>
      {showBrowse && (
        <FileBrowse
          itemIds={itemIds}
          onFiled={(folder) => {
            setShowBrowse(false);
            onSuccess?.({ ...folder, workspaceName: "", workspaceFolderId: 0, workspaceFolderName: "" });
          }}
        />
      )}
    </>
  );
};

export default Filing;
